// Mock data for Social Dispute Co Website

export const firmInfo = {
  name: "Social Dispute Co",
  tagline: "Formal Dispute Preparation & Escalation",
  phone: "808-301-2134",
  email: "intake@socialdisputeco.com",
  website: "socialdisputeco.com",
  instagram: "@socialdisputeco",
  address: "Los Angeles, CA"
};

export const services = [
  {
    id: 1,
    title: "Intake & Case Review",
    description: "We assess your situation. Made a mistake? We help you acknowledge it properly. Wrongly accused or hacked? We build your defense.",
    icon: "ClipboardCheck"
  },
  {
    id: 2,
    title: "Documentation Preparation",
    description: "Organized case documentation prepared for submission through official platform appeal channels.",
    icon: "FileText"
  },
  {
    id: 3,
    title: "Business Impact Statement",
    description: "We establish your account as a profitable part of your livelihood and the real financial damage caused.",
    icon: "TrendingUp"
  },
  {
    id: 4,
    title: "Policy-Aware Formatting",
    description: "Documentation structured with relevant policy context to support your appeal submission.",
    icon: "Scale"
  },
  {
    id: 5,
    title: "Evidence Building",
    description: "For hacked accounts: login records, communication history, identity verification—everything for an airtight case.",
    icon: "FolderSearch"
  },
  {
    id: 6,
    title: "Submission Guidance",
    description: "We help organize documentation for submission through official platform appeal channels.",
    icon: "Send"
  }
];

export const platforms = [
  { name: "Facebook", icon: "Facebook", color: "#1877F2" },
  { name: "Instagram", icon: "Instagram", color: "#E4405F" },
  { name: "YouTube", icon: "Youtube", color: "#FF0000" },
  { name: "TikTok", icon: "Music2", color: "#000000" },
  { name: "Twitter/X", icon: "Twitter", color: "#1DA1F2" },
  { name: "LinkedIn", icon: "Linkedin", color: "#0A66C2" }
];

export const stats = [
  { value: "8+", label: "Platforms Covered" },
  { value: "USA", label: "Based Service" },
  { value: "✓", label: "Professional Review" },
  { value: "✓", label: "References Available" }
];

export const testimonials = [
  {
    id: 1,
    name: "Sarah M.",
    role: "Business Owner",
    content: "Their documentation found procedural errors I missed. Professional and thorough.",
    rating: 5
  },
  {
    id: 2,
    name: "Michael T.",
    role: "Content Creator",
    content: "Professional, organized, and clear about what they do. The case packet made the submission process easier to follow.",
    rating: 5
  },
  {
    id: 3,
    name: "Jennifer K.",
    role: "Restaurant Owner",
    content: "Upfront about what they could do. Documentation was far better than anything I could've done.",
    rating: 5
  }
];

export const pricing = [
  {
    id: 1,
    tier: "Assessment",
    price: "$99",
    originalPrice: null,
    promoLabel: "Low Risk Start",
    description: "We look at your case and tell you if we can help",
    features: [
      "Complete case analysis",
      "Policy violation review",
      "Evidence assessment",
      "Written evaluation report",
      "Full action playbook",
      "Fight them yourself with our guidance"
    ],
    timeline: "Our Work: 3 Business Days",
    note: "Get a full playbook — go fight them yourself or hire us to handle everything",
    popular: false,
    isAssessment: true
  },
  {
    id: 2,
    tier: "Full Service",
    price: "$399",
    originalPrice: "$888",
    promoLabel: "Best Value",
    description: "We handle everything",
    features: [
      "Everything in Assessment",
      "Professionally reviewed documentation",
      "Certified mail to platform legal",
      "Case tracking with unique ID",
      "Email support",
      "Follow-up letter if denied"
    ],
    timeline: "Our Work: 5-7 Business Days",
    note: null,
    popular: true
  },
  {
    id: 3,
    tier: "Priority",
    price: "$499",
    originalPrice: "$1,333",
    promoLabel: "Fastest Prep",
    description: "Expedited documentation prep",
    features: [
      "Everything in Full Service",
      "Expedited preparation",
      "Priority support",
      "Direct phone support",
      "Dedicated case manager"
    ],
    timeline: "Our Work: 2-3 Business Days",
    note: null,
    popular: false
  }
];

export const faqItems = [
  {
    question: "Can permanently banned accounts be reviewed?",
    answer: "Yes — many platform bans begin with automated detection. In some cases, additional documentation or escalation can prompt human review. When the full context—ownership, timeline, policy alignment—is clearly documented, some cases receive additional review that standard appeals don't trigger."
  },
  {
    question: "Does waiting hurt my case?",
    answer: "Potentially, yes. Platforms have time limits on appeals, and evidence (like login records or communications) becomes harder to gather over time. Early action typically improves your options. We recommend starting your case review as soon as possible."
  },
  {
    question: "Do you provide outcome guarantees?",
    answer: "No. Social Dispute Co. prepares documentation and provides submission guidance but does not control platform decisions. Outcomes depend on platform review and the facts of each case."
  },
  {
    question: "What is your refund policy?",
    answer: "See our refund policy page for details. Refunds may be available in certain circumstances when documentation is completed but the appeal is unsuccessful."
  },
  {
    question: "Are you affiliated with Meta, TikTok, or any platform?",
    answer: "No. Social Dispute Co is an independent documentation service. We are not affiliated with any social media platform. Platforms make final decisions and we do not guarantee outcomes."
  },
  {
    question: "Do you contact platforms on my behalf?",
    answer: "We prepare documentation and guidance for submission through official platform channels."
  },
  {
    question: "What if I actually violated the rules?",
    answer: "We help you acknowledge the situation properly and document any corrective actions. This provides context for your appeal submission. Outcomes depend on platform review."
  },
  {
    question: "How is this different from filing my own appeal?",
    answer: "Standard appeals go through automated systems and customer support. We help organize your timeline, evidence, and policy-relevant context into structured documentation for submission through official appeal channels."
  },
  {
    question: "What if my account was hacked?",
    answer: "We compile evidence—login records, IP addresses, identity verification—and organize documentation to support your appeal submission."
  },
  {
    question: "What do you need from me to start?",
    answer: "Any notices/emails from the platform, the affected handle(s), date of action, and a brief timeline of what happened. Screenshots help. We'll review everything in your free case evaluation."
  }
];

export const intakeFormFields = {
  personalInfo: [
    { name: "clientName", label: "Full Name", type: "text", required: true },
    { name: "email", label: "Email Address", type: "email", required: true },
    { name: "phone", label: "Phone Number", type: "tel", required: true }
  ],
  accountInfo: [
    { name: "platform", label: "Social Media Platform", type: "select", required: true, options: ["Facebook", "Instagram", "YouTube", "TikTok", "Twitter/X", "LinkedIn", "Other"] },
    { name: "accountName", label: "Account Name/Handle", type: "text", required: true },
    { name: "accountType", label: "Account Type", type: "select", required: true, options: ["Personal", "Business", "Both"] },
    { name: "associatedEmail", label: "Email Associated with Account", type: "email", required: true },
    { name: "associatedPhone", label: "Phone Associated with Account", type: "tel", required: false }
  ],
  suspensionInfo: [
    { name: "suspensionDate", label: "Date of Suspension", type: "date", required: true },
    { name: "suspensionReason", label: "Reason Given for Suspension", type: "textarea", required: true },
    { name: "additionalInfo", label: "Additional Relevant Information", type: "textarea", required: false },
    { name: "hasAppealed", label: "Have you already appealed?", type: "select", required: true, options: ["Yes", "No"] },
    { name: "appealOutcome", label: "Appeal Outcome (if applicable)", type: "textarea", required: false }
  ]
};
